import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { signup } from "../api/auth";
import { useAuth } from "../stores/auth";

export default function Signup() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    office_name: "",
    owner_name: "",
    owner_email: "",
    phone: "",
    password: "",
    pc_count: 5,
  });

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const data = await signup(form);
      setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        mfaRequired: data.mfa_required,
        user: data.user,
        tenant: data.tenant,
        role: "owner",
      });
      navigate("/dashboard");
    } catch (e: unknown) {
      const detail = extractError(e);
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">Set up your office</h1>
        <p className="mt-2 text-slate-600">
          Connect every computer in your office in 15 minutes. No credit card needed for the first 14 days.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <Field
            label="Office name"
            value={form.office_name}
            onChange={(v) => update("office_name", v)}
            placeholder="Acme Dental Clinic"
            required
          />
          <Field
            label="Your name"
            value={form.owner_name}
            onChange={(v) => update("owner_name", v)}
            placeholder="Juan Dela Cruz"
            required
          />
          <Field
            label="Email"
            type="email"
            value={form.owner_email}
            onChange={(v) => update("owner_email", v)}
            placeholder="you@office.com"
            required
          />
          <Field
            label="Phone (optional)"
            value={form.phone}
            onChange={(v) => update("phone", v)}
            placeholder="+63 917 000 0000"
          />
          <Field
            label="Password (12+ characters)"
            type="password"
            value={form.password}
            onChange={(v) => update("password", v)}
            required
          />
          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="pc_count">
              How many computers in your office?
            </label>
            <input
              id="pc_count"
              type="number"
              min={1}
              max={50}
              value={form.pc_count}
              onChange={(e) => update("pc_count", Number(e.target.value))}
              className="mt-1 w-full rounded-lg border border-slate-300 p-3"
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              You can change this later. We'll match you to a plan automatically.
            </p>
          </div>

          {error ? (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-slate-900 p-3 font-medium text-white disabled:opacity-50"
          >
            {submitting ? "Creating your office…" : "Start free trial"}
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-600">
          Already have an office?{" "}
          <Link to="/login" className="font-medium text-slate-900 underline">
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}

type FieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
};

function Field({ label, value, onChange, type = "text", placeholder, required }: FieldProps) {
  const id = `field-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="mt-1 w-full rounded-lg border border-slate-300 p-3"
      />
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: Record<string, unknown> } }).response;
    const data = response?.data;
    if (data) {
      if (typeof data.detail === "string") return data.detail;
      const firstField = Object.entries(data)[0];
      if (firstField) {
        const [, val] = firstField;
        if (Array.isArray(val) && typeof val[0] === "string") return val[0];
        if (typeof val === "string") return val;
      }
    }
  }
  return "Something went wrong. Please try again.";
}
